import { useEffect, useRef, useState } from 'react'
import { getLive, type LiveFixture, type LiveSnapshot } from '../api/football'
import { refreshResults } from '../seasonData'
import type { DataMode } from '../dataMode'

/**
 * Polls our own `/api/live`.
 *
 * The client interval here is **independent of** the provider polling interval. Clients
 * hitting us every 10s costs us nothing; we hit the provider every 60s. That decoupling is the
 * whole architecture.
 */

/** Fast cadence, while something is actually happening. */
export const LIVE_POLL_SECONDS = 10
/** Slow cadence, after a few consecutive empty responses. */
export const IDLE_POLL_SECONDS = 60
/** How many empty responses before backing off. */
export const EMPTY_RESPONSES_BEFORE_BACKOFF = 3

export interface LivePollController {
  /** In-play fixtures as of the last non-304 response. */
  fixtures: LiveFixture[]
  /** The server's clock, for countdowns — never trust the local one for a shared deadline. */
  serverTime: string | null
  /**
   * `serverTime − Date.now()`, measured at the instant the poll landed.
   *
   * Computed here rather than by each consumer because this is the only place that holds both
   * terms at the same instant. Add it to a local reading and a device with a skewed clock counts
   * down to the same deadline as everyone else; 0 until the first poll lands, which is the
   * previous behaviour exactly.
   */
  serverOffsetMs: number
  /** True when the server is serving live data it knows is late. */
  stale: boolean
}

export interface UseLivePollOptions {
  /** Nothing polls until the app has loaded and the user is signed in. */
  ready: boolean
  /**
   * The current gameweek's kickoffs, comma-joined. A string rather than an array so it is stable
   * by value: an array literal would be a new identity on every render, and the effect below
   * would restart the poll each time.
   */
  kickoffKey: string
  /**
   * Which world the server is in; null until `/me` lands.
   *
   * Only `replica` reads it, and only to skip `isPlausibleLiveWindow` — see
   * `shouldGateOnLiveWindow`.
   */
  dataMode?: DataMode | null
  /**
   * Called after a fixture reached full time and the results store was refreshed. Must be
   * identity-stable across renders (a `useReducer` dispatch is), because it is an effect
   * dependency — an inline arrow would restart the poll on every render.
   */
  onResultsRefreshed?: () => void
  /** Injected in tests. */
  now?: () => number
  /**
   * How the next poll is scheduled; returns a cancel function. Injected rather than stubbed
   * globally, because replacing `setTimeout` process-wide also breaks the test renderer.
   */
  schedule?: (fn: () => void, ms: number) => () => void
}

/** Module-level so the default is identity-stable — an inline `() => {}` would restart the
 *  effect on every render, which is the loop this hook must never enter. */
const noop = () => {}

const defaultSchedule = (fn: () => void, ms: number) => {
  const timer = setTimeout(fn, ms)
  return () => clearTimeout(timer)
}

/** Opens 15 minutes before the first kickoff, closes 4 hours after the last. Never 3am Wednesday. */
export function isPlausibleLiveWindow(kickoffs: string[], nowMs: number): boolean {
  if (kickoffs.length === 0) return false
  const times = kickoffs.map((kickoff) => Date.parse(kickoff)).filter((time) => !Number.isNaN(time))
  if (times.length === 0) return false
  const first = Math.min(...times)
  const last = Math.max(...times)
  return nowMs >= first - 15 * 60_000 && nowMs <= last + 4 * 60 * 60_000
}

/**
 * Replica mode polls flat out: no window gate, no empty-response backoff.
 *
 * Both savings above assume the server's clock is this browser's clock — precisely what replica
 * mode violates. Aim a preset at gameweek 4 and `isPlausibleLiveWindow` compares "real today"
 * against "a kickoff three real weeks out", shuts, and never opens: `serverOffsetMs` is set from a
 * `getLive` response, so a closed gate starves the value that would reopen it. Skipping the gate
 * is the fix; feeding it the offset cannot be.
 *
 * The backoff fails for a different reason: nothing is in play until the rail jumps the clock, by
 * which time the streak has dropped the cadence to 60s — and one press has to show a live card
 * within one `LIVE_POLL_SECONDS` tick.
 *
 * Costs nothing where it applies: replica is a localhost hop. `mock` and `live` are untouched.
 */
export function pollsUnconditionally(dataMode: DataMode | null | undefined): boolean {
  return dataMode === 'replica'
}

export function useLivePoll({
  ready,
  kickoffKey,
  dataMode = null,
  onResultsRefreshed = noop,
  now = Date.now,
  schedule: scheduleTimeout = defaultSchedule,
}: UseLivePollOptions): LivePollController {
  const [fixtures, setFixtures] = useState<LiveFixture[]>([])
  const [serverTime, setServerTime] = useState<string | null>(null)
  const [serverOffsetMs, setServerOffsetMs] = useState(0)
  const [stale, setStale] = useState(false)

  const etag = useRef<string | null>(null)
  const emptyStreak = useRef(0)
  const previousStatuses = useRef(new Map<string, string>())
  // The two test seams are captured once, at mount. Keeping them out of the effect's dependency
  // list is deliberate: a caller passing an inline `now={() => …}` would otherwise hand the
  // effect a fresh identity on every render, restarting the poll in a loop.
  const nowRef = useRef(now)
  const scheduleRef = useRef(scheduleTimeout)

  useEffect(() => {
    if (!ready) return
    const kickoffs = kickoffKey === '' ? [] : kickoffKey.split(',')
    let cancelled = false
    let cancelTimer: (() => void) | null = null

    /** Anything that left the in-play set has finished (or been voided) — refresh the results. */
    async function reconcileFinished(snapshot: LiveSnapshot) {
      const current = new Map(snapshot.fixtures.map((fixture) => [fixture.id, fixture.status]))
      let settled = false
      for (const [id, status] of previousStatuses.current) {
        if (!current.has(id) && (status === 'live' || status === 'halftime')) settled = true
      }
      previousStatuses.current = current
      if (!settled) return
      try {
        await refreshResults()
        if (!cancelled) onResultsRefreshed()
      } catch {
        // A failed results refresh is a stale scoreline, not a broken app; the next full-time
        // transition (or a reload) picks it up.
      }
    }

    const alwaysPoll = pollsUnconditionally(dataMode)

    async function poll() {
      if (cancelled) return
      // Never poll at 3am on a Wednesday — unless the clock deciding that is the wrong clock.
      if (!alwaysPoll && !isPlausibleLiveWindow(kickoffs, nowRef.current())) {
        schedule(IDLE_POLL_SECONDS)
        return
      }
      try {
        const { snapshot, etag: nextEtag } = await getLive(etag.current)
        if (cancelled) return
        etag.current = nextEtag
        if (snapshot) {
          // A 304 leaves state untouched and triggers no re-render at all — that is the point of
          // sending If-None-Match.
          setFixtures(snapshot.fixtures)
          setServerTime(snapshot.serverTime)
          // Both terms read at the same instant, so the browser's own skew cancels out of the
          // subtraction. An unparseable time leaves the last good offset in place.
          const serverMs = Date.parse(snapshot.serverTime)
          if (!Number.isNaN(serverMs)) setServerOffsetMs(serverMs - nowRef.current())
          setStale(snapshot.stale)
          await reconcileFinished(snapshot)
          emptyStreak.current = snapshot.fixtures.length === 0 ? emptyStreak.current + 1 : 0
        }
      } catch {
        // Back off on failure the same way as on quiet: a server that is down is not a reason to
        // hammer it.
        emptyStreak.current += 1
      }
      schedule(
        !alwaysPoll && emptyStreak.current >= EMPTY_RESPONSES_BEFORE_BACKOFF
          ? IDLE_POLL_SECONDS
          : LIVE_POLL_SECONDS
      )
    }

    function schedule(seconds: number) {
      if (cancelled) return
      cancelTimer = scheduleRef.current(() => void poll(), seconds * 1000)
    }

    function onVisibilityChange() {
      cancelTimer?.()
      cancelTimer = null
      // Users leave tabs open for days; a hidden tab must cost nothing.
      if (document.visibilityState === 'hidden') return
      // Resume with an immediate fetch, not after a full interval.
      void poll()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    void poll()

    return () => {
      cancelled = true
      cancelTimer?.()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
    // `dataMode` is a dependency because it arrives asynchronously from `/me`: the first render
    // sees null and gates like production, and the poll has to be restarted once the answer is
    // `replica`. It is a string, so this restarts once, not per render.
  }, [ready, kickoffKey, dataMode, onResultsRefreshed])

  return { fixtures, serverTime, serverOffsetMs, stale }
}
